import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";

import { api, queries, unwrap, type AirframeDetail } from "@/api";
import { useAdminView } from "@/adminView";
import { AirframeMap } from "@/components/AirframeMap";
import { SharedCockpit } from "@/components/SharedCockpit";
import { ConditionBar } from "@/components/Bar";
import { ErrorNote } from "@/components/ErrorNote";
import { FlightSteps } from "@/components/FlightSteps";
import { HandoverBlock } from "@/components/Handover";
import { rowFor, useLocalActions } from "@/components/OnThisPc";
import { useHost } from "@/host/host";
import { hostQueries } from "@/host/queries";
import { MAINTENANCE, capitalize, eventSummary, formatDateTime, formatHours, formatInProgress, formatMaintenance, formatMinutes, formatRelative, formatRemaining, formatSeconds, leaseStatus, windshieldLine } from "@/lib/format";
import { LandingCell } from "@/components/FlightRecorder";

export const Route = createFileRoute("/airframes/$id/")({
  component: Overview,
});

/** The job buttons of the overview: inside the app, an aircraft held on this PC asks the app, which reads the parked aircraft (decision 129); elsewhere the platform. */
function MaintenanceButtons({ id, onApi, pending }: { id: string; onApi: (action: string) => void; pending: boolean }) {
  const host = useHost();
  if (!host) return <MaintenanceButtonList start={onApi} pending={pending} note={null} />;
  return <MaintenanceButtonsUnderHost id={id} onApi={onApi} pending={pending} />;
}

function MaintenanceButtonsUnderHost({ id, onApi, pending }: { id: string; onApi: (action: string) => void; pending: boolean }) {
  const host = useHost()!;
  const status = useQuery(hostQueries.status(host));
  const actions = useLocalActions();
  const queryClient = useQueryClient();
  const row = rowFor(status.data, id);
  const here = row && row.local?.lease ? row : null;
  if (!here) return <MaintenanceButtonList start={onApi} pending={pending} note={null} />;
  const note = actions.notes[here.uuid] ?? null;
  return (
    <MaintenanceButtonList
      start={(action) => actions.maintain.mutate({ uuid: here.uuid, action }, { onSuccess: () => queryClient.invalidateQueries({ queryKey: ["airframe", id] }) })}
      pending={actions.maintain.isPending}
      note={note}
    />
  );
}

function MaintenanceButtonList({ start, pending, note }: { start: (action: string) => void; pending: boolean; note: { ok: boolean; text: string } | null }) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2" data-testid="maintenance-actions">
        {MAINTENANCE.map((m) => (
          <button key={m.action} type="button" className="btn btn-sm" onClick={() => start(m.action)} disabled={pending} data-testid={`maintain-${m.action}`} title={m.hint}>
            {m.label}, {m.duration}
          </button>
        ))}
      </div>
      {note ? (
        <p className={`text-xs ${note.ok ? "notice" : "alert"}`} data-testid="maintenance-note">
          {note.text}
        </p>
      ) : null}
    </div>
  );
}

/** The overview (F413, decision 055): now, the totals, the condition, the maintenance, the recent flights, the timeline. */
function Overview() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const airframe = useQuery(queries.airframe(id));
  const me = useQuery(queries.me());
  const adminView = useAdminView();
  const flights = useQuery(queries.flights(id));
  const events = useQuery({ ...queries.events(id), enabled: !!airframe.data?.your_role || adminView });
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["airframe", id] });
    queryClient.invalidateQueries({ queryKey: ["airframes"] });
    queryClient.invalidateQueries({ queryKey: ["members", id] });
  };
  const release = useMutation({
    mutationFn: async (lid: string) => unwrap(await api.POST("/api/v1/leases/{lid}/release", { params: { path: { lid } }, body: {} })),
    onSuccess: invalidate,
  });
  // A maintenance job by the holder (F406): the same account as the lease,
  // so no companion token; the platform judges the parked aircraft.
  const maintain = useMutation({
    mutationFn: async (action: string) => unwrap(await api.POST("/api/v1/airframes/{id}/maintenance", { params: { path: { id } }, body: { action } })),
    onSuccess: invalidate,
  });
  if (!airframe.data) return null;
  const a = airframe.data;
  if (!a.your_role && !adminView) return <Introduction airframe={a} />;
  const l = a.ledger;
  const c = l.condition;
  const lease = a.lease;
  const manages = a.your_role === "owner" || a.your_role === "captain";
  const status = leaseStatus(lease);
  const isHolder = !!lease && !!me.data && lease.holder.id === me.data.user.id;
  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-3">
        <section className="card space-y-4 lg:col-span-2">
          <div className="flex items-center gap-3">
            <h2 className="text-[1.0714rem] font-semibold">Now</h2>
            {lease ? <span className="muted text-xs">heartbeat {formatRelative(lease.last_heartbeat)}; {isHolder ? "yours" : `${lease.holder.name}'s`} for {formatRemaining(lease.expires_at)} more</span> : null}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-0.5">
              <span className="kicker">Who has it</span>
              <span className={`font-semibold ${status.tone === "ok" ? "text-ok" : "text-warn"}`} data-testid="lease-line">
                {status.label}
              </span>
              {lease ? <span className="muted text-xs">booked {formatRelative(lease.acquired_at)}</span> : <span className="muted text-xs">ready to be booked</span>}
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="kicker">Last landing</span>
              <span className="font-semibold">{l.last_landing ? l.last_landing.icao : "none yet"}</span>
              <span className="muted text-xs">{l.last_landing ? formatDateTime(l.last_landing.utc) : "no flight in the log"}</span>
            </div>
          </div>
          {a.livery ? (
            <p className="muted text-xs" data-testid="livery-line">
              Livery <span className="text-ink">{a.livery.name}</span>, {(a.livery.bytes / 1_048_576).toFixed(1)} MB
              {a.livery.uploaded_by ? ` offered by ${a.livery.uploaded_by.name}` : ""}: the pilots who take the liveries owners offer install it when they book the aircraft.
            </p>
          ) : null}
          <div className="flex flex-wrap items-center gap-3">
            {lease && manages && me.data && !isHolder ? (
              <button type="button" className="btn btn-danger btn-sm" onClick={() => release.mutate(lease.id)} disabled={release.isPending} data-testid="revoke-lease">
                Take the aircraft back
              </button>
            ) : null}
            {lease?.preflight_at && !isHolder ? (
              <span className="pill pill-ok" data-testid="now-preflight-signed">
                pre-flight signed {formatRelative(lease.preflight_at)}
              </span>
            ) : null}
          </div>
          <ErrorNote error={release.error} />
          {me.data ? (
            <FlightSteps
              airframeId={a.id}
              reg={a.identity.reg}
              meId={me.data.user.id}
              booking={lease ? { holder: lease.holder, acquired_at: lease.acquired_at, flying: lease.flying, signed: !!lease.preflight_at, signed_at: lease.preflight_at } : null}
              last={{ pilotId: l.last_pilot?.id ?? null, at: l.last_commit_at ?? null, seq: l.latest_seq ?? null }}
            />
          ) : null}
          {me.data ? <HandoverBlock airframeId={a.id} reg={a.identity.reg} meId={me.data.user.id} isHolder={isHolder} booked={!!lease} last={{ pilotId: l.last_pilot?.id ?? null, at: l.last_commit_at ?? null }} /> : null}
          {/* The condition's figures for the administrator alone (decision 120): the pilots see the aircraft as it looks. */}
          {c.details ? (
            <div className="space-y-2 border-t border-line pt-4" data-testid="condition">
            <ConditionBar label="Windshield" ratio={Math.max(c.windshield_bugs, c.windshield_dust ?? 0, c.windshield_spots ?? 0, c.windshield_streaks ?? 0)} level={c.level_windshield} testId="windshield-bugs" />
            <p className="muted pl-[7.75rem] text-xs" data-testid="windshield-line">
              {windshieldLine(c, new Date(), !lease)}
            </p>
            <ConditionBar label="Exterior dirt" ratio={c.exterior_dirt} level={c.level_exterior} />
            <ConditionBar label="Cabin" ratio={c.cabin_dirt} level={c.level_cabin} />
            <ConditionBar label="Cockpit grime" ratio={c.cockpit_grime ?? 0} level={c.level_cockpit ?? 0} testId="cockpit-grime" />
            <p className="muted text-xs">
              {c.profile === "off" ? "The condition model is off for this airframe." : `${capitalize(c.profile)} profile. `}
              {c.profile !== "off" ? `Last wash ${formatMaintenance(c.last_wash_exterior)}; windshield cleaned ${formatMaintenance(c.last_clean_windshield)}; cabin cleaned ${formatMaintenance(c.last_clean_cabin)}; cockpit wiped ${formatMaintenance(c.last_wipe_cockpit)}.` : ""}
            </p>
            </div>
          ) : null}
          {a.description ? <p className="whitespace-pre-line text-sm">{a.description}</p> : null}
        </section>
        <div className="space-y-4">
          <section className="card space-y-3">
            <h2 className="text-[1.0714rem] font-semibold">Totals</h2>
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[0.9286rem]">
              <dt className="muted">Airframe hours</dt>
              <dd className="text-right tnum" data-testid="total-hours">
                {formatHours(l.total_hours)}
              </dd>
              <dt className="muted">Flights</dt>
              <dd className="text-right tnum" data-testid="cycles">
                {l.cycles}
              </dd>
              <dt className="muted">Pilots</dt>
              <dd className="text-right tnum">{l.distinct_pilots}</dd>
              <dt className="muted">Hard landings</dt>
              <dd className="text-right tnum" data-testid="hard-landings">
                {l.hard_landings ?? 0}
              </dd>
              <dt className="muted">Door cycles</dt>
              <dd className="text-right tnum">{l.door_cycles ?? 0}</dd>
              <dt className="muted">APU hobbs</dt>
              <dd className="text-right tnum">{formatSeconds(l.counters.apu_hobbs_s)}</dd>
              <dt className="muted">ISI hours</dt>
              <dd className="text-right tnum">{formatSeconds(l.counters.isi_run_time_s)}</dd>
              <dt className="muted">Uptime</dt>
              <dd className="text-right tnum">{formatSeconds(l.counters.abus_uptime_s)}</dd>
              <dt className="muted">Tech log entries</dt>
              <dd className="text-right tnum">{l.versions}</dd>
            </dl>
            <Link to="/airframes/$id/ledger" params={{ id }} className="muted text-xs hover:underline">
              The full logbook
            </Link>
          </section>
          <section className="card space-y-2.5">
            <h2 className="text-[1.0714rem] font-semibold">Maintenance</h2>
            <p className="muted text-xs">Only while parked, engines off, dry, above freezing, with the main door open. The pilot who has the aircraft asks here or from the app.</p>
            {c.in_progress ? (
              <p className="alert alert-ok text-sm" data-testid="maintenance-in-progress">
                {capitalize(formatInProgress(c.in_progress))}. The aircraft is not to be flown meanwhile.
              </p>
            ) : isHolder ? (
              <MaintenanceButtons id={id} onApi={(action) => maintain.mutate(action)} pending={maintain.isPending} />
            ) : (
              <p className={`text-xs ${lease ? "text-warn" : "muted"}`}>
                {lease ? `Unavailable: ${lease.holder.name} has the aircraft.` : a.your_role ? "Book the aircraft from the app to clean it." : "Members clean it while they have it."}
              </p>
            )}
            <ErrorNote error={maintain.error} />
          </section>
        </div>
      </div>
      <SharedCockpit airframe={a} meId={me.data?.user.id ?? null} />
      <AirframeMap airframe={a} ledgerPosition={l?.position as { lat?: number; lon?: number; hdg_deg?: number | null } | undefined} />
      <div className="grid gap-4 lg:grid-cols-3">
        <section className="card-flush lg:col-span-2">
          <div className="card-head">
            <h2 className="text-[1.0714rem] font-semibold">Recent flights</h2>
            <Link to="/airframes/$id/ledger" params={{ id }} className="muted ml-auto text-xs hover:underline">
              All {flights.data?.total ?? 0}
            </Link>
          </div>
          <ErrorNote error={flights.error} />
          {flights.data && flights.data.items.length === 0 ? <p className="muted p-5 text-sm">No flight in the log yet.</p> : null}
          {flights.data && flights.data.items.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table" data-testid="flights">
                <thead>
                  <tr>
                    <th>Takeoff</th>
                    <th>Route</th>
                    <th>Pilot</th>
                    <th className="text-right">Time</th>
                    <th className="text-right">Fuel</th>
                    <th>Landing</th>
                    <th aria-label="Open" />
                  </tr>
                </thead>
                <tbody>
                  {flights.data.items.slice(0, 6).map((f) => (
                    <tr key={f.id} className="table-row-link" onClick={() => navigate({ to: "/airframes/$id/flights/$fid", params: { id, fid: f.id } })} data-testid="recent-flight">
                      <td className="tnum">{formatDateTime(f.takeoff_utc)}</td>
                      <td className="font-semibold">
                        <Link to="/airframes/$id/flights/$fid" params={{ id, fid: f.id }} className="row-link" onClick={(e) => e.stopPropagation()}>
                          <span className="ident">{f.takeoff_icao || "----"}</span> to <span className="ident">{f.landing_icao || "----"}</span>
                        </Link>
                      </td>
                      <td>{f.pilot ?? <span className="muted">history</span>}</td>
                      <td className="text-right tnum">{formatMinutes(f.enroute_min)}</td>
                      <td className="text-right tnum">{f.fuel_used_lb.toLocaleString("en-US")} lb</td>
                      <td>
                        <LandingCell flight={f} />
                      </td>
                      <td className="text-right">
                        <span className="row-open" aria-hidden="true">
                          Open ›
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
        {a.your_role ? (
          <section className="card-flush">
            <div className="card-head">
              <h2 className="text-[1.0714rem] font-semibold">Timeline</h2>
              <Link to="/airframes/$id/history" params={{ id }} className="muted ml-auto text-xs hover:underline" data-testid="timeline-everything">
                Everything
              </Link>
            </div>
            <ul className="divide-y divide-line text-[0.9286rem]" data-testid="timeline-short">
              {(events.data?.items ?? []).slice(0, 8).map((e) => (
                <li key={e.id} className="flex gap-3 px-5 py-2">
                  <span className="muted w-20 shrink-0 text-xs tnum">{formatRelative(e.created_at)}</span>
                  <span className={e.type === "version.refused" ? "text-warn" : ""}>{eventSummary(e) || e.type}</span>
                </li>
              ))}
              {events.data && events.data.items.length === 0 ? <li className="muted px-5 py-3">Nothing yet.</li> : null}
            </ul>
          </section>
        ) : null}
      </div>
    </div>
  );
}

/**
 * What a pilot who is not a member sees (F488, decision 102 item 5): the
 * identity and the owner are in the header, the way to join above; here
 * the description, where it stands, how much it has lived, and whether it
 * can be booked now. The life of the airframe (the condition, the tech log,
 * the flights, the maintenance) is the members'.
 */
function Introduction({ airframe: a }: { airframe: AirframeDetail }) {
  const l = a.ledger;
  const where = a.location
    ? a.location.name
      ? `${a.location.icao ? `${a.location.icao} · ` : ""}${a.location.name}`
      : "a field off the airports"
    : "not returned yet";
  const booked = a.lease ? `Booked until ${formatDateTime(a.lease.expires_at)}${a.lease.flying ? ", flying now" : ""}` : "Free to book";
  const join = a.join_policy === "open" ? "Open to every pilot: join and book." : a.join_policy === "request" ? "Ask the owner to join." : "By the owner's invitation.";
  return (
    <div className="grid gap-4 lg:grid-cols-3" data-testid="introduction">
      <section className="card space-y-4 lg:col-span-2">
        <h2 className="text-[1.0714rem] font-semibold">About this airframe</h2>
        {a.description ? <p className="whitespace-pre-line text-sm">{a.description}</p> : <p className="muted text-sm">The owner wrote nothing about it yet.</p>}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <div className="flex flex-col gap-0.5">
            <span className="kicker">Where it stands</span>
            <span className="font-semibold" data-testid="intro-where">
              {where}
            </span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="kicker">Airframe hours</span>
            <span className="font-semibold tnum" data-testid="intro-hours">
              {formatHours(l.total_hours)}
            </span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="kicker">Flights</span>
            <span className="font-semibold tnum">{l.cycles}</span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="kicker">Now</span>
            <span className="font-semibold" data-testid="intro-booked">
              {booked}
            </span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="kicker">Last landing</span>
            <span className="font-semibold" data-testid="intro-landing">
              {l.last_landing ? `${l.last_landing.icao}, ${formatDateTime(l.last_landing.utc)}` : "no flight in the log"}
            </span>
          </div>
        </div>
        <p className="muted text-xs">{join} The condition, the tech log, the flights and the maintenance are for the members.</p>
      </section>
      <section className="card space-y-2">
        <h2 className="text-[1.0714rem] font-semibold">Owner</h2>
        <p className="text-sm">{a.owner.name}</p>
        <p className="muted text-xs">Hot Start Challenger 650 {a.identity.cl650_version}, livery {a.identity.livery || "Default"}.</p>
      </section>
    </div>
  );
}
